// =========================================================
// Navbar.jsx
// ORION BEACH HOTEL · GUAJIRA
// NAVBAR PREMIUM · NAVEGACIÓN POR SECCIONES
// =========================================================

import {
  useEffect,
  useState,
} from "react";

import "../styles/Navbar.css";


// =========================================================
// CONFIGURACIÓN DE NAVEGACIÓN
// =========================================================

const NAVIGATION = [

  {
    id: "hero",
    label: "Inicio",
  },

  {
    id: "habitaciones",
    label: "Habitaciones",
  },

  {
    id: "historia",
    label: "Sobre nosotros",
  },

  {
    id: "clientes",
    label: "Clientes satisfechos",
  },

  {
    id: "ubicacion",
    label: "Ubicación",
  },

];


// =========================================================
// NAVBAR
// =========================================================

export default function Navbar() {

  const [
    open,
    setOpen
  ] = useState(false);


  const [
    scrolled,
    setScrolled
  ] = useState(false);


  // =======================================================
  // SCROLL EFFECT
  // =======================================================

  useEffect(() => {

    const handleScroll = () => {

      setScrolled(
        window.scrollY > 40
      );

    };


    window.addEventListener(
      "scroll",
      handleScroll,
      {
        passive: true,
      }
    );


    // Ejecutar inmediatamente
    // para detectar la posición inicial.

    handleScroll();


    return () => {

      window.removeEventListener(
        "scroll",
        handleScroll
      );

    };

  }, []);


  // =======================================================
  // CLOSE MENU ON RESIZE
  // =======================================================

  useEffect(() => {

    const handleResize = () => {

      if (
        window.innerWidth > 900
      ) {

        setOpen(false);

      }

    };


    window.addEventListener(
      "resize",
      handleResize
    );


    return () => {

      window.removeEventListener(
        "resize",
        handleResize
      );

    };

  }, []);


  // =======================================================
  // LOCK BODY WHEN MENU IS OPEN
  // =======================================================

  useEffect(() => {

    if (open) {

      document.body.classList.add(
        "menu-open"
      );

    } else {

      document.body.classList.remove(
        "menu-open"
      );

    }


    return () => {

      document.body.classList.remove(
        "menu-open"
      );

    };

  }, [open]);


  // =======================================================
  // SCROLL TO SECTION
  // =======================================================

  const scrollToSection = (
    sectionId
  ) => {

    const element =
      document.getElementById(
        sectionId
      );


    // -----------------------------------------------------
    // VALIDAR SECCIÓN
    // -----------------------------------------------------

    if (!element) {

      console.warn(
        `Sección #${sectionId} no encontrada.`
      );

      setOpen(false);

      return;

    }


    // -----------------------------------------------------
    // OBTENER ALTURA DEL NAVBAR
    // -----------------------------------------------------

    const navbar =
      document.querySelector(
        ".navbar"
      );


    const navbarHeight =
      navbar?.offsetHeight || 0;


    // -----------------------------------------------------
    // POSICIÓN ACTUAL DEL ELEMENTO
    // -----------------------------------------------------

    const elementPosition =
      element.getBoundingClientRect().top;


    // -----------------------------------------------------
    // CALCULAR POSICIÓN FINAL
    // -----------------------------------------------------

    const offsetPosition =
      elementPosition +
      window.scrollY -
      navbarHeight -
      12;


    // -----------------------------------------------------
    // SCROLL SUAVE
    // -----------------------------------------------------

    window.scrollTo({

      top:
        Math.max(
          0,
          offsetPosition
        ),

      behavior:
        "smooth",

    });


    // -----------------------------------------------------
    // CERRAR MENÚ MOBILE
    // -----------------------------------------------------

    setOpen(false);

  };


  // =======================================================
  // HANDLE NAVIGATION
  // =======================================================

  const handleNavigation = (
    event,
    sectionId
  ) => {

    event.preventDefault();

    scrollToSection(
      sectionId
    );

  };


  // =======================================================
  // LOGO CLICK
  // =======================================================

  const handleLogoClick = () => {

    scrollToSection(
      "hero"
    );

  };


  // =======================================================
  // LOGO KEYBOARD
  // =======================================================

  const handleLogoKeyDown = (
    event
  ) => {

    if (
      event.key === "Enter" ||
      event.key === " "
    ) {

      event.preventDefault();

      handleLogoClick();

    }

  };


  // =======================================================
  // MOBILE MENU
  // =======================================================

  const toggleMenu = () => {

    setOpen(
      (previous) =>
        !previous
    );

  };


  // =======================================================
  // RENDER
  // =======================================================

  return (

    <>

      {/* ===================================================
          HEADER
      =================================================== */}

      <header
        className={`
          navbar
          ${scrolled
            ? "navbar--scrolled"
            : ""
          }
          ${open
            ? "navbar--open"
            : ""
          }
        `}
      >

        <div
          className="
            navbar__container
          "
        >

          {/* =================================================
              LOGO
          ================================================= */}

          <div
            className="
              navbar__logo
            "

            onClick={
              handleLogoClick
            }

            onKeyDown={
              handleLogoKeyDown
            }

            role="button"

            tabIndex={0}

            aria-label="
              Ir al inicio de
              Hotel Orión Guajira
            "
          >

            <img
              src="/images/logo-orion.png"
              alt="Hotel Orión Guajira"
              className="
                navbar__logo-image
              "
            />

          </div>


          {/* =================================================
              NAVEGACIÓN PRINCIPAL
          ================================================= */}

          <nav
            className={`
              navbar__links
              ${open
                ? "active"
                : ""
              }
            `}

            aria-label="
              Navegación principal
            "
          >

            {NAVIGATION.map(
              (item) => (

                <a
                  key={item.id}

                  href={`#${item.id}`}

                  onClick={(event) =>
                    handleNavigation(
                      event,
                      item.id
                    )
                  }

                  className="
                    navbar__link
                  "
                >

                  {item.label}

                </a>

              )
            )}


            {/* =============================================
                MOBILE CTA
            ============================================= */}

            <button
              type="button"

              className="
                navbar__mobile-book
              "

              onClick={() =>
                scrollToSection(
                  "ubicacion"
                )
              }
            >

              <span>
                Cómo llegar
              </span>

              <span>
                →
              </span>

            </button>

          </nav>


          {/* =================================================
              DESKTOP CTA
          ================================================= */}

          <div
            className="
              navbar__actions
            "
          >

            <button
              type="button"

              className="
                navbar__booking
              "

              onClick={() =>
                scrollToSection(
                  "ubicacion"
                )
              }
            >

              <span>
                Cómo llegar
              </span>

              <span
                className="
                  navbar__booking-icon
                "
              >

                →

              </span>

            </button>

          </div>


          {/* =================================================
              HAMBURGER MENU
          ================================================= */}

          <button
            type="button"

            className={`
              navbar__toggle
              ${open
                ? "open"
                : ""
              }
            `}

            onClick={
              toggleMenu
            }

            aria-label={
              open
                ? "Cerrar menú"
                : "Abrir menú"
            }

            aria-expanded={
              open
            }

            aria-controls="
              main-navigation
            "
          >

            <span></span>
            <span></span>
            <span></span>

          </button>

        </div>

      </header>


      {/* =====================================================
          MOBILE OVERLAY
      ===================================================== */}

      {open && (

        <div
          className="
            navbar__overlay
          "

          onClick={() =>
            setOpen(false)
          }

          aria-hidden="true"
        />

      )}

    </>

  );

}